import React from "react";
import moment from "moment";
import { CalendarDays } from "lucide-react";
import Datepicker from "components/shared/Datepicker/Datepicker";
import { PurchaseOrderButton, PurchaseOrderFooter, PurchaseOrderSuccessModal } from "./PoDetailsElements";
import Input from "components/shared/InputTypes/Input/Input";
import styles from "./PoDisputeForm.module.scss";
import { strings } from "utils/constants/strings";

const DateFieldInput = React.forwardRef(({ value, onClick, placeholder }, ref) => (
  <Input
    ref={ref}
    label={strings.poDisputeDeliverByLabel}
    value={value || ""}
    placeholder={placeholder || strings.poDisputeSelectDatePlaceholder}
    icon={<CalendarDays size={16} color="#ef4f5f" aria-hidden style={{ pointerEvents: "none", flexShrink: 0 }} />}
    onChange={() => {}}
    onClick={onClick}
    fullWidth
    borderRadius="12px"
    style={{ cursor: "pointer" }}
  />
));
DateFieldInput.displayName = "DateFieldInput";

/**
 * Raise-dispute form shared by the desktop drawer (variant="drawer") and the
 * mobile page (variant="page"). All state lives in usePurchaseOrderDetailsLogic.
 */
const PoDisputeForm = ({ variant = "page", items, dispute}) => {
  const isDrawer = variant === "drawer";
  const submitButton = (
    <PurchaseOrderButton type="button" disabled={dispute.submitting} onClick={dispute.submit}>
      {dispute.submitting ? strings.poDisputeSubmitting : strings.poDisputeSubmitButton}
    </PurchaseOrderButton>
  );

  return (
    <div className={`${styles.form} ${isDrawer ? styles.drawer : ""}`}>
      <div className={styles.body}>
        {!isDrawer && <h2 className={`fs-20 fw-700 ${styles.title}`}>{strings.grnDisputeTitle}</h2>}

        <section className={styles.section}>
          <h3 className={`fs-16 fw-700 ${styles.sectionTitle}`}>
            {isDrawer ? strings.poDisputeSuggestedDeliveryDate : strings.poDisputeSuggestDeliveryDate}
          </h3>
          <Datepicker
            selected={dispute.proposedDeliveryDate ? moment(dispute.proposedDeliveryDate, "YYYY-MM-DD").toDate() : null}
            onChange={(date) => {
              dispute.setProposedDeliveryDate(date ? moment(date).format("YYYY-MM-DD") : "");
            }}
            minDate={moment().toDate()}
            dateFormat="dd-MM-yyyy"
            customInput={<DateFieldInput />}
          />
        </section>

        <section className={`${styles.section} ${styles.productSection}`}>
          <h3 className={`fs-16 fw-700 ${styles.sectionTitle}`}>
            {strings.grnDisputeProductDetails}
            {!isDrawer && <span className={styles.skuBadge}>{items.length} SKUs</span>}
          </h3>
          <div className={styles.productList}>
            {items.map((item, index) => {
              const unit = item.buy_measurement_unit || "unit";
              return (
                <article className={styles.product} key={item.id || item.product_number || index}>
                  <div className={styles.productIdentity}>
                    <img src={item.product_image || "/logos/po_creator_logo.svg"} alt="" />
                    <span className={`fw-500 ${styles.productName}`}>{item.product_name || "Product"}</span>
                  </div>
                  <div className={styles.productFields}>
                    <Input
                      label={strings.poDisputeOrderQuantityLabel}
                      disabled
                      value={String(Number(item.quantity_ordered || 0))}
                      suffix={unit}
                      fullWidth
                      borderRadius="12px"
                    />
                    <Input
                      label={`Price per ${unit}`}
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={dispute.proposedPrices[item.id] || ""}
                      onChange={(value) => dispute.setProposedPrice(item.id, value)}
                      suffix={`₹/${unit}`}
                      fullWidth
                      borderRadius="12px"
                    />
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        <section className={styles.section}>
          <label className={`fs-14 fw-600 ${styles.sectionTitle}`} htmlFor="dispute-remarks">
            {strings.grnDisputeShareDetails}
          </label>
          <textarea
            id="dispute-remarks"
            className={styles.remarks}
            value={dispute.remarks}
            placeholder={strings.grnDisputeDescriptionPlaceholder}
            onChange={(event) => dispute.setRemarks(event.target.value)}
          />
        </section>

        {(dispute.error || dispute.failed) && (
          <p className="fs-12 text-red-500">
            {dispute.error || strings.poDisputeSubmitError}
          </p>
        )}
      </div>

      {isDrawer ? (
        <div className={styles.drawerFooter}>{submitButton}</div>
      ) : (
        <PurchaseOrderFooter single>{submitButton}</PurchaseOrderFooter>
      )}

    </div>
  );
};

export default PoDisputeForm;
